import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { v4 } from 'uuid';
import {
  EditAndAcceptAnnotationRequest,
  EditAndRejectAnnotationRequest,
  IAcceptAnnotationRequest,
  IRejectAnnotationRequest,
} from '../../../modules/editor/models/Requests';
import { IBatchAnnotationApiService } from '../../../modules/editor/services/BatchAnnotationApi.service';
import { buildBounds, getLatLngsForGeojson } from '../../helpers/geometry/polygon.helpers';
import { ACDLError } from '../../models/error.model';
import { EventListeningDisposer, IEventBus } from '../../services/eventBus.service';
import { IProjectHubService } from '../../services/projectHub.service';
import { ITimerService } from '../../services/timer.service';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { IAnnotationService } from './annotation.service';
import { IAnnotationApiService } from './annotationApi.service';
import { AnnotationStatus, IAnnotationDto, IImage, IQuestionModelDto, ISegmentation, ISegmentationDto } from './annotations.interface';
import { IAnnotationsStore } from './annotations.store';
import { IAnnotationUiStore } from './annotationUi.store';
import { BaseAnnotationControl, IBaseAnnotationControl } from './baseAnnotationControl';
import { IFreeDrawSegmentationService } from './freeDrawSegmentation.service';
import { IImagesQueueService } from './imagesQueueServiceBase';
import { cleanClone, cleanClones, IQuestionModel, validateQuestion } from './question.model';
import { ReviewImagesQueueService } from './reviewImagesQueue.service';
import { IAnnotationTypeBl } from './submodules/annotationTypes/annotationType.bl';
import { ClarificationAddedEvent, ClarificationAddedEventType } from './submodules/clarifications/events/ClarificationAddedEvent';
import { IAnnotationReviewStore } from './submodules/review/annotationReview.store';
import { ReviewMode } from './submodules/review/models/reviewMode';
import { IReviewRejectionReasonBl } from './submodules/reviewRejectReasones/reviewRejectionReason.bl';
import { IUndoRedoHistory, UndoRedoHistory } from './undoRedoHistory.service';

export interface IAnnotationReviewService extends IBaseAnnotationControl {
  acceptAnnotationAsync(): Promise<void>;
  discardAnnotationsAsync(): Promise<void>;
  refreshRequested(): void;
  rejectAnnotationAsync(reason?: string): Promise<void>;
  reviewFinishedAsync(): Promise<void>;
  reviewStartedAsync(projectId: string, reviewImagesQueueService: IImagesQueueService): Promise<void>;
  cancelCorrectAsync(): Promise<void>;
  correctAcceptAnnotationAsync(): Promise<void>;
  correctDiscardAnnotationAsync(): Promise<void>;
  correctRejectAnnotationAsync(reason?: string): Promise<void>;
  navigateToCorrect(): void;
}

export namespace IAnnotationReviewService {
  export const $: interfaces.ServiceIdentifier<IAnnotationReviewService> = Symbol('ANNOTATION_REVIEW_SERVICE');
}

@injectable()
export class AnnotationReviewService extends BaseAnnotationControl implements IAnnotationReviewService {
  private projectId: string = '';

  private get reviewImagesQueueService(): ReviewImagesQueueService {
    return this.imagesQueueService as ReviewImagesQueueService;
  }

  private eventListenersDisposer: EventListeningDisposer[] = [];

  constructor(
    @inject(IProjectHubService.$) private readonly projectHubService: IProjectHubService,
    @inject(INotificationsService.$) public readonly notificationsService: INotificationsService,
    @inject(IAnnotationApiService.$) public readonly annotationApiService: IAnnotationApiService,
    @inject(IAnnotationService.$) public readonly annotationService: IAnnotationService,
    @inject(IAnnotationTypeBl.$) public readonly annotationTypeBl: IAnnotationTypeBl,
    @inject(IAnnotationUiStore.$) public readonly uiStore: IAnnotationUiStore,
    @inject(IFreeDrawSegmentationService.$) private readonly freeDrawSegmentationService: IFreeDrawSegmentationService,
    @inject(IUndoRedoHistory.$) private readonly undoRedoService: IUndoRedoHistory,
    @inject(IAnnotationReviewStore.$) private readonly reviewStore: IAnnotationReviewStore,
    @inject(IUndoRedoHistory.$) private readonly historyService: UndoRedoHistory,
    @inject(IAnnotationsStore.$) annotationsStore: IAnnotationsStore,
    @inject(ITimerService.$) public readonly timer: ITimerService,
    @inject(IReviewRejectionReasonBl.$) private readonly reviewRejectionReasonBl: IReviewRejectionReasonBl,
    @inject(IEventBus.$) public readonly eventBus: IEventBus,
    @inject(IBatchAnnotationApiService.$) private readonly batchAnnotationApiService: IBatchAnnotationApiService
  ) {
    super(uiStore, notificationsService, annotationsStore);

    makeObservable<AnnotationReviewService, 'loadAnnotations' | 'sendCorrectedAsync' | 'clarificationAddedListener'>(this, {
      setupAsync: action,
      loadAnnotations: action.bound,
      requestQuestionsAsync: action,
      refreshQuestionsIfNeeded: action,
      sendCorrectedAsync: action,
      correctRejectAnnotationAsync: action,
      updateReviewProgress: action,
      clarificationAddedListener: action.bound,
    });
  }
  handleImageDisplayed = (image: IImage): void => {
    this.loadAnnotations(image);
  };

  private addEventListeners(): void {
    this.eventListenersDisposer.push(
      this.eventBus.addListener<ClarificationAddedEvent>(async () => this.clarificationAddedListener(), ClarificationAddedEventType)
    );
  }

  private clearEventListeners(): void {
    this.eventListenersDisposer.forEach((d) => {
      d();
    });
    this.eventListenersDisposer = [];
  }

  async reviewStartedAsync(projectId: string, reviewImagesQueueService: ReviewImagesQueueService): Promise<void> {
    reviewImagesQueueService.setup(projectId);
    await this.setupAsync(projectId, reviewImagesQueueService);
  }

  async reviewFinishedAsync(): Promise<void> {
    const result = await this.annotationApiService.unlockUserImagesForProjectAsync(this.projectId);
    if (result instanceof ACDLError) {
      throw result;
    }
    this.dispose();
  }

  refreshRequested(): void {
    this.reviewImagesQueueService.start(true);
  }

  async cancelCorrectAsync(): Promise<void> {
    if (!(await this.freeDrawSegmentationService.clearAsync())) {
      return;
    }
    if (this.historyService.canUndo && this.annotationsStore.image) {
      this.loadAnnotations(this.annotationsStore.image);
    }
    this.switchModeToReview();
  }

  async correctAcceptAnnotationAsync(): Promise<void> {
    await this.sendCorrectedAsync(true);
  }

  async setupAsync(projectId: string, imagesQueueService: IImagesQueueService): Promise<void> {
    this.projectId = projectId;
    this.annotationsStore.projectId = projectId;
    this.reviewStore.setCurrentMode(ReviewMode.Review);
    this.imagesQueueService = imagesQueueService;
    await this.projectHubService.initializeAsync();
    this.timer.startTimer();
    this.initReactions();
    this.imagesQueueService.start(true);

    await Promise.all([this.annotationTypeBl.reviewStarted(this.projectId), this.requestQuestionsAsync(), this.requestRejectionReasonsAsync()]);

    await this.displayNextImage();

    this.addEventListeners();
  }

  private loadAnnotations(image: IImage): void {
    const annotationInfoResult = image.annotations;

    if (!annotationInfoResult) {
      return;
    }

    const annotations = annotationInfoResult.annotations;
    if (annotations === undefined) {
      throw new ACDLError('No annotations found', undefined);
    }

    this.annotationsStore.id = annotationInfoResult.id;
    const segmentations: ISegmentation[] = annotations.segmentations.map((s: ISegmentationDto) => {
      const feature = {
        ...s.feature,
        id: v4(),
        color: this.annotationService.getAnnotationTypeColor(s.feature.properties?.annotationTypeId),
        featureType: s.feature.properties?.featureType,
      };
      const latlngs = getLatLngsForGeojson(feature);

      return {
        feature,
        latlngs,
        id: s.id,
        questions: this.mapQuestions(s.feature.properties?.annotationTypeId, s.questions),
        bbox: buildBounds(latlngs),
        priority: s.priority,
      } as ISegmentation;
    });

    this.annotationsStore.setSegmentations(segmentations.filter((s) => s.feature.color !== ''));
    this.annotationsStore.questions.replace(this.mapQuestions(undefined, annotationInfoResult.annotations.questions));
    this.annotationService.deselectSegmentation();
  }

  private mapQuestions(annotationTypeId: string | undefined, questionsDtos: IQuestionModelDto[]): IQuestionModel[] {
    const currentQuestions = annotationTypeId
      ? this.annotationsStore.questions.filter((q) => q.scopes.includes(annotationTypeId))
      : this.annotationsStore.questions;

    const models: IQuestionModel[] = [];
    for (const currentQuestion of currentQuestions) {
      const existingAnswer = questionsDtos.find((x) => x.id === currentQuestion.id);
      models.push(cleanClone(currentQuestion, existingAnswer));
    }

    return models;
  }

  async requestQuestionsAsync(): Promise<void> {
    const result = await this.annotationApiService.requestQuestionsAsync(this.projectId);
    if (result instanceof Error) {
      throw result;
    }
    this.annotationsStore.questions.replace([...result]);
    this.annotationService.deselectSegmentation();
  }

  async requestRejectionReasonsAsync(): Promise<void> {
    await this.reviewRejectionReasonBl.getReasons(this.projectId);
  }

  refreshQuestionsIfNeeded(): void {
    if (this.projectHubService.didQuestionsChangedFor(this.annotationsStore.projectId)) {
      const result = this.projectHubService.popNewQuestionsFor(this.annotationsStore.projectId);
      this.annotationsStore.questions.replace(cleanClones(result));
      this.annotationService.deselectSegmentation();
    }
  }

  async rejectAnnotationAsync(reason: string = ''): Promise<void> {
    if (!this.annotationsStore.id) {
      throw 'Annnotation Id can not be empty';
    }

    const request: IRejectAnnotationRequest = {
      reason,
      id: this.annotationsStore.id,
      duration: this.timer.duration,
    };

    const result = await this.batchAnnotationApiService.rejectAsync(request);
    await this.reloadBasedOnResult(result);

    if (reason) {
      this.reviewRejectionReasonBl.handleRejection(reason);
    }
  }

  async acceptAnnotationAsync(): Promise<void> {
    if (!this.annotationsStore.id) {
      throw 'Annnotation Id can not be empty';
    }

    const request: IAcceptAnnotationRequest = {
      id: this.annotationsStore.id,
      duration: this.timer.duration,
    };

    const result = await this.batchAnnotationApiService.acceptAsync(request);
    await this.reloadBasedOnResult(result);
  }

  async discardAnnotationsAsync(): Promise<void> {
    if (!this.annotationsStore.id) {
      throw 'Annnotation Id can not be empty';
    }

    const request = { id: this.annotationsStore.id, duration: this.timer.duration };
    const result = await this.batchAnnotationApiService.discardDuringReviewAsync(request);
    await this.reloadBasedOnResult(result);
  }

  async correctDiscardAnnotationAsync(): Promise<void> {
    if (!this.annotationsStore.id) {
      throw 'Annnotation Id can not be empty';
    }

    const request = { id: this.annotationsStore.id, duration: this.timer.duration };
    const result = await this.batchAnnotationApiService.discardDuringReviewEditAsync(request);
    await this.reloadBasedOnResult(result);
    this.switchModeToReview();
  }

  private async reloadBasedOnResult(result: void | ACDLError): Promise<void> {
    if (result instanceof ACDLError) {
      await this.handleErrors(result);
      if (result.apiErrorResponse?.errorCodes.includes('ANNOTATION_NOT_FOUND')) {
        this.annotationsStore.image = undefined;
        this.imagesQueueService.areAnyImagesToLoad = false;
        return;
      }
    }
    await this.displayNextImage();
  }

  async displayNextImage(): Promise<void> {
    this.refreshQuestionsIfNeeded();
    this.annotationTypeBl.handleDisplayNextImageInReview();
    this.freeDrawSegmentationService.clear();
    this.annotationService.clearAnnotations();
    this.annotationService.clearAnswers();
    await this.updateReviewProgress();
    this.displayImageFromQueue();
    this.timer.startTimer();
  }

  private async sendCorrectedAsync(accept: boolean, rejectionReason?: string): Promise<void> {
    if (!(await this.freeDrawSegmentationService.clearAsync())) {
      return;
    }
    if (!this.annotationsStore.id || !this.annotationService.validateAnnotations()) {
      await this.annotationTypeBl.handleSubmitReview();
      return;
    }

    this.annotationService.fixRectangles();

    const correctedAnnotation = this.mapAnnotationDto();

    if (!this.annotationsStore.image?.annotations) {
      throw 'Annotations can not be empty';
    }

    this.annotationsStore.image.annotations = {
      ...this.annotationsStore.image.annotations,
      annotations: correctedAnnotation,
      status: accept ? AnnotationStatus.ACCEPTED : AnnotationStatus.REJECTED,
    };

    let result = undefined;

    if (accept) {
      const request = new EditAndAcceptAnnotationRequest(correctedAnnotation, this.timer.duration, this.annotationsStore.id);
      result = await this.batchAnnotationApiService.editAndAcceptAsync(request);
    } else {
      const request = new EditAndRejectAnnotationRequest(rejectionReason, correctedAnnotation, this.timer.duration, this.annotationsStore.id);
      result = await this.batchAnnotationApiService.editAndRejectAsync(request);
    }

    if (result instanceof ACDLError) {
      await this.handleErrors(result);
    }

    await this.displayNextImage();
    this.switchModeToReview();
  }

  async handleErrors(e: ACDLError): Promise<void> {
    if (e.isBadRequestWithCode(['ANNOTATION_NOT_FOUND'])) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.WARNING, 'operation_was_unsuccessful_dataset_changed'));
    } else if (e.isBadRequestWithCode(['IMAGE_ALREADY_LOCKED'])) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, '', 'image_is_locked_by_another_user'));
    } else {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, '', 'something_went_wrong'));
    }
  }

  async correctRejectAnnotationAsync(reason?: string): Promise<void> {
    if (this.reviewStore.currentMode === ReviewMode.Correct) {
      await this.sendCorrectedAsync(false, reason);
    } else {
      await this.rejectAnnotationAsync(reason);
    }
    this.switchModeToReview();
  }

  dispose(): void {
    super.dispose();
    this.annotationService.clear();
    this.imagesQueueService.clear();
    this.imagesQueueService.dispose();
    this.annotationTypeBl.reviewFinished();
    this.clearEventListeners();
  }

  async updateReviewProgress(): Promise<void> {
    const result = await this.annotationApiService.getReviewProgressAsync(this.projectId);
    if (result instanceof ACDLError) {
      return;
    }
    this.annotationsStore.reviewProgress = result;
  }

  navigateToCorrect(): void {
    this.reviewStore.setCurrentMode(ReviewMode.Correct);
    this.annotationService.deselectSegmentation();
  }

  private switchModeToReview(): void {
    this.reviewStore.setCurrentMode(ReviewMode.Review);
    this.annotationService.deselectSegmentation();
    this.undoRedoService.clearDrawingHistory();
    this.undoRedoService.clearHistory();
  }

  private async clarificationAddedListener(): Promise<void> {
    await this.displayNextImage();
    this.switchModeToReview();
  }

  private mapAnnotationDto(id?: string): IAnnotationDto {
    const annotationId = id ?? this.annotationsStore.id;

    if (!annotationId) {
      throw 'Annnotation Id can not be empty';
    }

    return {
      id: annotationId,
      questions: this.annotationsStore.questions,
      segmentations: this.annotationsStore.segmentations.map((s) => ({
        id: s.id ?? v4(),
        questions: s.questions.filter((q) => validateQuestion(q)),
        feature: {
          type: s.feature.type,
          properties: s.feature.properties,
          geometry: s.feature.geometry,
        },
        priority: s.priority,
      })),
    };
  }
}
